import { useState, useEffect } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import { Stack, Container, Group, Loader, Alert, Divider, ScrollArea } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import leadSchemaDefinition, { type Lead, type UpdateLead } from '../../../../schemas/lead.schema';
import {
  useLeadRepository,
  useLeadStatusRepository,
  useLeadSourceRepository,
  useSalesAreaRepository,
} from '@ayocore/erp/crm/client/rpc';
import { useUserRepository } from '@ayocore/platform/identity/client/rpc';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';

/**
 * EditLeadForm - Lead editing form with clean pattern
 */
export function EditLeadForm({
  leadId,
  onLeadUpdated,
}: {
  leadId: number;
  onLeadUpdated?: () => void;
}) {
  const leadRepository = useLeadRepository();
  const leadStatusRepository = useLeadStatusRepository();
  const leadSourceRepository = useLeadSourceRepository();
  const salesAreaRepository = useSalesAreaRepository();
  const userRepository = useUserRepository();
  const [initialData, setInitialData] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const SchemaForm = useSchemaForm(leadSchemaDefinition.updateSchema, { hideDescriptions: true });

  // Проверка прав на редактирование лида
  const [canEdit, setCanEdit] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Update form values when data loads
  useEffect(() => {
    if (initialData) {
      SchemaForm.setInitialData(initialData);
    }
  }, [initialData]);

  // Load existing lead data
  useEffect(() => {
    const loadLead = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const lead = await leadRepository.findById(leadId);

        if (!lead) {
          setLoadError(`Lead with ID ${leadId} not found`);
          return;
        }

        setInitialData(lead);
      } catch (error) {
        logger.error('Failed to load lead:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load lead');
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      loadLead();
    }
  }, [leadId]);

  // Проверка прав на редактирование для текущего лида
  useEffect(() => {
    const checkPermissions = async () => {
      if (!leadId || isNaN(leadId)) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const canUpdateResult = await leadRepository.canUpdate(leadId);
        setCanEdit(canUpdateResult.allowed);
      } catch (err) {
        logger.error('Failed to check update permission:', err);
        setCanEdit(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    checkPermissions();
  }, [leadId]);

  const handleSubmit = async (values: UpdateLead) => {
    // Update lead using repository
    const updatedLead = await leadRepository.update(leadId, values);

    // Show success message
    SchemaForm.setSuccess(`Лид "${updatedLead.title}" успешно обновлен!`);

    // Call the callback to refresh list and close modal after a brief delay
    setTimeout(() => {
      if (onLeadUpdated) {
        onLeadUpdated();
      }
    }, 1500);

    return { success: true };
  };

  // Loading state
  if (loading) {
    return (
      <Container size='md' py='xl'>
        <Group justify='center'>
          <Loader size='md' />
          <span>Loading lead...</span>
        </Group>
      </Container>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Container size='md' py='xl'>
        <Alert color='red' title='Ошибка'>
          {loadError}
        </Alert>
      </Container>
    );
  }

  // Don't render form until data is loaded
  if (!initialData) {
    return (
      <Container size='md' py='xl'>
        <Group justify='center'>
          <Loader size='md' />
          <span>Loading lead data...</span>
        </Group>
      </Container>
    );
  }

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs' pb='md'>
            <SchemaForm.Messages />

            {/* Basic Information */}
            <Group grow align='flex-start'>
              <SchemaForm.Field.title disabled={!canEdit || permissionsLoading} />
              <PartnerSelect
                value={SchemaForm.values.partner_id?.toString() || ''}
                onChange={value =>
                  SchemaForm.setFieldValue('partner_id', value ? parseInt(value) : null)
                }
                disabled={!canEdit || permissionsLoading || Boolean(initialData.partner_id)}
              />
            </Group>

            <SchemaForm.Field.description disabled={!canEdit || permissionsLoading} />

            <Divider />

            {/* Status and Management */}
            <Group grow>
              <SchemaForm.Field.lead_status_id
                repo={leadStatusRepository}
                displayField='name'
                placeholder='Выберите статус лида...'
                disabled={!canEdit || permissionsLoading}
              />
              <SchemaForm.Field.manager_id
                repo={userRepository}
                displayField='name'
                placeholder='Выберите менеджера...'
                disabled={!canEdit || permissionsLoading}
              />
            </Group>

            <Divider />

            {/* Атрибутика лида: канал привлечения и территория */}
            <Group grow>
              <SchemaForm.Field.lead_source_id
                repo={leadSourceRepository.filterActive().orderBy('sort_order', 'asc')}
                displayField='name'
                placeholder='Выберите источник...'
                disabled={!canEdit || permissionsLoading}
              />
              <SchemaForm.Field.sales_area_id
                repo={salesAreaRepository}
                displayField='name'
                placeholder='Выберите регион...'
                disabled={!canEdit || permissionsLoading}
              />
            </Group>

            <Divider />

            {/* Financial Details */}
            <Group grow>
              <SchemaForm.Field.amount disabled={!canEdit || permissionsLoading} />
              <SchemaForm.Field.probability disabled={!canEdit || permissionsLoading} />
            </Group>

            <Divider />

            {/* Dates */}
            <Group grow>
              <SchemaForm.Field.expected_close_date disabled={!canEdit || permissionsLoading} />
              <SchemaForm.Field.actual_close_date disabled={!canEdit || permissionsLoading} />
            </Group>

            <Divider />

            {/* Additional Information */}
            <SchemaForm.Field.notes disabled={!canEdit || permissionsLoading} />
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'transparent',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={() => onLeadUpdated?.()} label='Отмена' />
          <SchemaForm.buttons.submit
            label='Обновить лид'
            disabled={!canEdit || permissionsLoading}
          />
        </Group>
      </div>
    </form>
  );
}

export default EditLeadForm;
